import * as React from 'react';
import {useEffect, useRef} from 'react';
import {KatBox, KatButton, KatInput, KatModal} from '@amzn/katal-react';
import {StringUtil} from 'src/app/util/StringUtil';

export interface ReopenTicketDialogProps {
    /** Whether the dialog is visible. */
    visible: boolean;
    /** Called when the dialog is dismissed (Cancel / X / backdrop). */
    onCancel: () => void;
    /** Called with the entered ticket id when Submit is clicked. */
    onSubmit: (ticketId: string) => void;
    /** Disables the buttons while the reopen request is in flight. */
    submitting?: boolean;
}

/**
 * Ticket-id confirmation dialog for the Reopen flow on the Lighthouse review popup.
 * Mirrors the classic review page's ReOpenInputDialog UI (Confirmation title, required
 * Ticket Id input, Cancel/Submit), but is a plain controlled component wired to the
 * new SubmitDecision API instead of the legacy event bus + ReviewDecisionStore.
 */
const ReopenTicketDialog: React.FC<ReopenTicketDialogProps> = ({visible, onCancel, onSubmit, submitting = false}) => {
    const ticketIdRef = useRef<any>(null);

    // The KatModal stays mounted (only `visible` toggles), so the uncontrolled input would
    // otherwise carry a previously typed ticket id into the next reopen attempt. Reset it on
    // every open; a failed submit keeps the dialog open, so the value survives for retries.
    useEffect(() => {
        if (visible && ticketIdRef.current) {
            ticketIdRef.current.value = '';
        }
    }, [visible]);

    const handleSubmit = () => {
        const ticket = ticketIdRef.current?.value;
        if (StringUtil.isNullOrEmpty(ticket)) {
            alert('please input Ticket id link!');
            return;
        }
        onSubmit(ticket);
    };

    return (
        <KatModal visible={visible} data-testid="reopen-ticket-modal" onClose={onCancel}>
            <span slot="title">Confirmation</span>
            <KatBox>
                <KatInput label="Ticket Id" className="ticket-input" ref={ticketIdRef}
                          data-testid="reopen-ticket-id-input" required={true}></KatInput>
            </KatBox>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant={'secondary'} label={'Cancel'} disabled={submitting}
                           onClick={onCancel} data-testid="reopen-cancel-btn"></KatButton>
                <KatButton label={'Submit'} disabled={submitting}
                           onClick={handleSubmit} data-testid="reopen-submit-btn"></KatButton>
            </div>
        </KatModal>
    );
};

export default ReopenTicketDialog;
